import React, { PropsWithChildren } from 'react'
import Link from 'next/link'

import {
  StyledButton,
  StyledButtonDanger,
  StyledDangerTextButton,
  StyledLeidButton,
  StyledLeidLightButton,
  StyledLeidLightLinkButton,
  StyledLeidLinkButton,
  StyledLeidRedButton,
  StyledLeidRedLinkButton,
  StyledLeidWhiteOutlinedButton,
  StyledLeidWhiteOutlinedLinkButton,
  StyledLeidYellowButton,
  StyledLeidYellowLinkButton,
  StyledLinkButton,
  StyledOutlinedButton,
  StyledOutlinedLinkButton,
  StyledSecondaryButton,
  StyledSecondaryLinkButton,
  StyledTextButton,
  StyledTextLink,
  StyledWhiteButton,
  StyledWhiteLinkButton,
  StyledWhiteOutlinedButton,
  StyledWhiteOutlinedLinkButton,
} from './styles'

type Props = {
  href?: string
  type?: 'button' | 'reset' | 'submit' | undefined
  isDisabled?: boolean
  onClick?: (event?: any) => void
  as?: string
  height?: string
  className?: string
  id?: string
  openNewTab?: boolean
}

export const Button: React.FC<PropsWithChildren<Props>> = ({
  children,
  href,
  type = 'button',
  isDisabled = false,
  onClick,
  height,
  className,
}) => {
  if (href) {
    return (
      <Link href={href} passHref onClick={onClick}>
        <StyledLinkButton height={height} isDisabled={isDisabled} className={className}>
          {children}
        </StyledLinkButton>
      </Link>
    )
  }

  return (
    <StyledButton isDisabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledButton>
  )
}

export const ButtonSecondary: React.FC<PropsWithChildren<Props>> = ({
  children,
  href,
  type = 'button',
  isDisabled = false,
  onClick,
  height,
  className,
}) => {
  if (href) {
    return (
      <Link href={href} passHref legacyBehavior>
        <StyledSecondaryLinkButton height={height} isDisabled={isDisabled} className={className}>
          {children}
        </StyledSecondaryLinkButton>
      </Link>
    )
  }

  return (
    <StyledSecondaryButton isDisabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledSecondaryButton>
  )
}

export const ButtonWhite: React.FC<PropsWithChildren<Props>> = ({
  children,
  isDisabled = false,
  onClick,
  type,
  href,
  as,
  height,
  className,
  id,
}) => {
  if (href) {
    return (
      <Link href={href} as={as} passHref legacyBehavior>
        <StyledWhiteLinkButton height={height} isDisabled={isDisabled} className={className} id={id}>
          {children}
        </StyledWhiteLinkButton>
      </Link>
    )
  }

  return (
    <StyledWhiteButton isDisabled={isDisabled} type={type} onClick={onClick} className={className} id={id}>
      {children}
    </StyledWhiteButton>
  )
}

export const ButtonWhiteOutlined: React.FC<PropsWithChildren<Props>> = ({
  children,
  isDisabled = false,
  onClick,
  type,
  href,
  as,
  height,
  className,
  id,
}) => {
  if (href) {
    return (
      <Link href={href} as={as} passHref legacyBehavior>
        <StyledWhiteOutlinedLinkButton height={height} isDisabled={isDisabled} className={className} id={id}>
          {children}
        </StyledWhiteOutlinedLinkButton>
      </Link>
    )
  }

  return (
    <StyledWhiteOutlinedButton
      height={height}
      isDisabled={isDisabled}
      type={type}
      onClick={onClick}
      className={className}
      id={id}
    >
      {children}
    </StyledWhiteOutlinedButton>
  )
}

export const ButtonDanger: React.FC<PropsWithChildren<Props>> = ({
  children,
  type = 'button',
  isDisabled = false,
  onClick,
  className,
}) => {
  return (
    <StyledButtonDanger disabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledButtonDanger>
  )
}

export const ButtonText: React.FC<
  PropsWithChildren<Props & { isLight?: boolean; isSmall?: boolean; isDimmed?: boolean }>
> = ({ href, children, onClick, type, isLight = false, isSmall = false, isDimmed = false, as, className }) => {
  if (href) {
    return (
      <Link href={href} as={as} passHref legacyBehavior>
        <StyledTextLink isLight={isLight} isSmall={isSmall} isDimmed={isDimmed} className={className}>
          {children}
        </StyledTextLink>
      </Link>
    )
  }

  return (
    <StyledTextButton
      type={type}
      onClick={onClick}
      isLight={isLight}
      isSmall={isSmall}
      isDimmed={isDimmed}
      className={className}
    >
      {children}
    </StyledTextButton>
  )
}

export const ButtonDangerText: React.FC<PropsWithChildren<Props & { isLight?: boolean }>> = ({
  children,
  onClick,
  className,
}) => {
  return (
    <StyledDangerTextButton onClick={onClick} className={className}>
      {children}
    </StyledDangerTextButton>
  )
}

export const ButtonOutlined: React.FC<
  PropsWithChildren<Props & { noBorder?: boolean; isSelected?: boolean; isDimmed?: boolean; isOff?: boolean }>
> = ({
  children,
  isDisabled = false,
  onClick,
  type,
  href,
  as,
  height,
  className,
  id,
  noBorder = false,
  isSelected = false,
  isDimmed = false,
  isOff = false,
}) => {
  if (href) {
    return (
      <Link href={href} as={as} passHref legacyBehavior>
        <StyledOutlinedLinkButton
          height={height}
          isDisabled={isDisabled}
          className={className}
          id={id}
          noBorder={noBorder}
          isSelected={isSelected}
          isDimmed={isDimmed}
          isOff={isOff}
        >
          {children}
        </StyledOutlinedLinkButton>
      </Link>
    )
  }

  return (
    <StyledOutlinedButton
      isDisabled={isDisabled}
      type={type}
      onClick={onClick}
      className={className}
      id={id}
      noBorder={noBorder}
      isSelected={isSelected}
      isDimmed={isDimmed}
      isOff={isOff}
    >
      {children}
    </StyledOutlinedButton>
  )
}

// New Leid buttons

export const LeidButton: React.FC<PropsWithChildren<Props>> = ({
  children,
  href,
  type = 'button',
  isDisabled = false,
  onClick,
  height,
  className,
  openNewTab,
}) => {
  if (href) {
    return (
      <Link href={href} passHref legacyBehavior target={openNewTab ? '_blank' : undefined}>
        <StyledLeidLinkButton height={height} isDisabled={isDisabled} className={className}>
          {children}
        </StyledLeidLinkButton>
      </Link>
    )
  }

  return (
    <StyledLeidButton isDisabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledLeidButton>
  )
}

export const LeidLeightButton: React.FC<PropsWithChildren<Props>> = ({
  children,
  href,
  type = 'button',
  isDisabled = false,
  onClick,
  height,
  className,
}) => {
  if (href) {
    return (
      <Link href={href} passHref legacyBehavior>
        <StyledLeidLightLinkButton height={height} isDisabled={isDisabled} className={className}>
          {children}
        </StyledLeidLightLinkButton>
      </Link>
    )
  }

  return (
    <StyledLeidLightButton isDisabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledLeidLightButton>
  )
}

// Yellow button
export const LeidYellowButton: React.FC<PropsWithChildren<Props>> = ({
  children,
  href,
  type = 'button',
  isDisabled = false,
  onClick,
  height,
  className,
}) => {
  if (href) {
    return (
      <Link href={href} passHref legacyBehavior>
        <StyledLeidYellowLinkButton height={height} isDisabled={isDisabled} className={className}>
          {children}
        </StyledLeidYellowLinkButton>
      </Link>
    )
  }

  return (
    <StyledLeidYellowButton isDisabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledLeidYellowButton>
  )
}

// Yellow button
export const LeidRedButton: React.FC<PropsWithChildren<Props>> = ({
  children,
  href,
  type = 'button',
  isDisabled = false,
  onClick,
  height,
  className,
}) => {
  if (href) {
    return (
      <Link href={href} passHref legacyBehavior>
        <StyledLeidRedLinkButton height={height} isDisabled={isDisabled} className={className}>
          {children}
        </StyledLeidRedLinkButton>
      </Link>
    )
  }

  return (
    <StyledLeidRedButton isDisabled={isDisabled} type={type} onClick={onClick} className={className}>
      {children}
    </StyledLeidRedButton>
  )
}

// Outlined button
export const LeidButtonWhiteOutlined: React.FC<PropsWithChildren<Props>> = ({
  children,
  isDisabled = false,
  onClick,
  type,
  href,
  as,
  height,
  className,
  id,
}) => {
  if (href) {
    return (
      <Link href={href} as={as} passHref legacyBehavior>
        <StyledLeidWhiteOutlinedLinkButton height={height} isDisabled={isDisabled} className={className} id={id}>
          {children}
        </StyledLeidWhiteOutlinedLinkButton>
      </Link>
    )
  }

  return (
    <StyledLeidWhiteOutlinedButton
      height={height}
      isDisabled={isDisabled}
      type={type}
      onClick={onClick}
      className={className}
      id={id}
    >
      {children}
    </StyledLeidWhiteOutlinedButton>
  )
}
